import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { confirmPasswordReset } from "firebase/auth";
import { auth } from "../../services/firebase";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import { FaLock } from "react-icons/fa";
import "./ConfirmPassword.css";
import {
  isMaintenanceMode,
  MAINTENANCE_ACTION_BLOCK_TEXT,
} from "../../utils/maintenance";

const ConfirmPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Map Firebase error codes to friendly Arabic messages
  const mapFirebaseError = (err) => {
    const code = err?.code || "";
    switch (code) {
      case "auth/invalid-action-code":
      case "auth/expired-action-code":
        return "The password reset link is invalid or has expired. Please request a new link from the forgot password page.";
      case "auth/weak-password":
        return "The password is too weak. Please choose a stronger password.";
      case "auth/network-request-failed":
        return "Unable to connect to the server. Please try again later.";
      default:
        return "An error occurred while resetting the password. Please try again or request a new link.";
    }
  };
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [oobCode, setOobCode] = useState("");

  useEffect(() => {
    // Get the OOB code from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get("oobCode");
    if (code) {
      setOobCode(code);
    } else {
      setError(
        "The password reset link is invalid. Please request a new link from the forgot password page."
      );
    }
  }, [location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    // Block reset during maintenance mode
    if (isMaintenanceMode()) {
      setError(MAINTENANCE_ACTION_BLOCK_TEXT);
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, formData.password);
      navigate("/signin", {
        state: {
          message:
            "Password has been reset successfully. Please sign in with your new password.",
        },
      });
    } catch (err) {
      setError(mapFirebaseError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 bg-gradient">
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col xs={12} sm={10} md={8} lg={6} xl={5}>
            <div className="bg-white shadow-lg rounded-4 overflow-hidden">
              <div className="brand-header text-center py-4">
                <img src="/logo.svg" alt="Logo" width="64" height="64" />
                <p className="brand-subtitle">Consumer Protection Networks</p>
                <div className="portal-badge">Reset Password</div>
              </div>

              <div className="p-4 p-md-5">
                <div className="text-center mb-4">
                  <h2 className="welcome-text">Create New Password</h2>
                  <p className="text-muted">Please enter your new password</p>
                </div>

                {error && <Alert variant="danger">{error}</Alert>}

                <Form onSubmit={handleSubmit} className="mb-4">
                  <Form.Group className="mb-4">
                    <div className="position-relative">
                      <FaLock className="position-absolute top-50 translate-middle-y ms-3 text-primary" />
                      <Form.Control
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="New Password"
                        className="ps-5 custom-input"
                        required
                        autoComplete="new-password"
                        disabled={isMaintenanceMode() || loading}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <div className="position-relative">
                      <FaLock className="position-absolute top-50 translate-middle-y ms-3 text-primary" />
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        placeholder="Confirm New Password"
                        className="ps-5 custom-input"
                        required
                        autoComplete="new-password"
                        disabled={isMaintenanceMode() || loading}
                      />
                    </div>
                  </Form.Group>

                  <Button
                    type="submit"
                    variant="primary"
                    className="w-100 py-3 custom-button"
                    disabled={loading || isMaintenanceMode()}
                  >
                    {loading ? (
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                        aria-hidden="true"
                      />
                    ) : (
                      "Reset Password"
                    )}
                  </Button>
                  <div className="d-flex justify-content-center align-items-center mt-3">
                    <Link
                      to="/forgot-password"
                      className="text-decoration-none forgot-link"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ConfirmPassword;
